// src/lib/db.js
// All Firestore operations. Every shop's data lives under /tenants/{shopId}/...
// This guarantees complete data isolation between businesses.

import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  getDocs, getDoc, query, where, orderBy, limit,
  onSnapshot, runTransaction, serverTimestamp, writeBatch, Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

// ─── Tenant (Shop) helpers ────────────────────────────────────────────────────

export const tenantRef  = (shopId) => doc(db, 'tenants', shopId);
export const colRef     = (shopId, col) => collection(db, 'tenants', shopId, col);
export const docRef     = (shopId, col, id) => doc(db, 'tenants', shopId, col, id);

export async function createTenant(shopId, data) {
  await writeBatch(db)
    .set(tenantRef(shopId), { ...data, createdAt: serverTimestamp() })
    .commit();
}

export async function getTenant(shopId) {
  const snap = await getDoc(tenantRef(shopId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateTenant(shopId, data) {
  await updateDoc(tenantRef(shopId), { ...data, updatedAt: serverTimestamp() });
}

// ─── Payment data hygiene ─────────────────────────────────────────────────────

/**
 * Strip cardholder data before anything is persisted.
 *
 * PCI-DSS forbids storing the CVV/CVC at all — not encrypted, not briefly,
 * not ever — and storing the full PAN drags the whole Firestore project into
 * PCI scope. The card is authorised on the payment terminal; all we legitimately
 * need afterwards is enough to identify it on a receipt or refund.
 *
 * This lives at the DB boundary rather than in the form so that no future
 * screen can reintroduce the leak by forgetting to sanitise.
 */
export function scrubBilling(billing = {}) {
  const { cardNumber, ccv, cvv, cvc, ...safe } = billing;
  const digits = String(cardNumber || '').replace(/\D/g, '');
  return {
    ...safe,
    cardLast4: digits ? digits.slice(-4) : '',
    cardBrand: cardBrandOf(digits),
    // expDate and auth are retained — needed for refunds and reconciliation,
    // and useless without the PAN.
  };
}

function cardBrandOf(digits) {
  if (!digits) return '';
  if (/^4/.test(digits)) return 'Visa';
  if (/^5[1-5]|^2(2[2-9]|[3-6]|7[01]|720)/.test(digits)) return 'Mastercard';
  if (/^3[47]/.test(digits)) return 'Amex';
  if (/^6(011|5|4[4-9])/.test(digits)) return 'Discover';
  return 'Card';
}

// ─── Orders ───────────────────────────────────────────────────────────────────

/**
 * Create an order and claim its number atomically.
 *
 * The previous read-then-write sequence let two terminals saving at the same
 * moment both read lastOrderNumber = 1041 and both write order #1042. Inside a
 * transaction Firestore detects the contention and retries, so numbers are
 * unique even with the whole shop taking orders at once.
 */
export async function createOrder(shopId, order) {
  const ref = doc(colRef(shopId, 'orders')); // id generated client-side

  try {
    await runTransaction(db, async (tx) => {
      const tenantSnap = await tx.get(tenantRef(shopId));
      if (!tenantSnap.exists()) throw new Error(`Shop ${shopId} not found`);

      const orderNumber = (tenantSnap.data().lastOrderNumber || 1000) + 1;

      tx.set(ref, {
        ...order,
        billing: scrubBilling(order.billing),
        orderNumber,
        status: order.status || 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      tx.update(tenantRef(shopId), { lastOrderNumber: orderNumber });
    });
  } catch (e) {
    console.error('[createOrder] transaction failed. shopId:', shopId, e);
    throw e;
  }

  return ref.id;
}

export async function updateOrder(shopId, orderId, data) {
  await updateDoc(docRef(shopId, 'orders', orderId), {
    ...data,
    // Re-scrub on edit — an order screen could otherwise write a raw PAN back.
    ...(data.billing && { billing: scrubBilling(data.billing) }),
    updatedAt: serverTimestamp()
  });
}

export async function getOrders(shopId, filters = {}) {
  let q = query(colRef(shopId, 'orders'), orderBy('createdAt', 'desc'));
  if (filters.type)   q = query(q, where('type', '==', filters.type));
  if (filters.status) q = query(q, where('status', '==', filters.status));
  if (filters.limit)  q = query(q, limit(filters.limit));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function getOrder(shopId, orderId) {
  const snap = await getDoc(docRef(shopId, 'orders', orderId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function deleteOrder(shopId, orderId) {
  await deleteDoc(docRef(shopId, 'orders', orderId));
}

// ─── Workboard ────────────────────────────────────────────────────────────────

/**
 * Live feed of everything the workboard needs. Two listeners, merged:
 *
 *  1. The most recent `max` orders by createdAt. Catches legacy orders written
 *     before `fulfillment` existed, orders with no date at all, and anything
 *     currently overdue.
 *  2. Every order dated today or later, by fulfillment.neededDate. This is the
 *     one that matters for advance bookings — a wedding taken in November for
 *     March would fall outside window (1) once enough orders pile up behind it,
 *     and vanishing from the board is precisely the bug we're fixing.
 *
 * Returns the unsubscribe function.
 */
export function subscribeRecentOrders(shopId, { max = 400, onData, onError } = {}) {
  const today = new Date();
  const p = n => String(n).padStart(2, '0');
  const todayStr = `${today.getFullYear()}-${p(today.getMonth() + 1)}-${p(today.getDate())}`;

  const recent = {}, upcoming = {};
  let gotRecent = false;

  // Merge by doc id — an order in both windows must not appear twice.
  const emit = () => { if (gotRecent) onData?.(Object.values({ ...upcoming, ...recent })); };

  const fail = (where) => (err) => {
    console.error(`[subscribeRecentOrders:${where}]`, err);
    onError?.(err);
  };
  const collect = (bag) => (snap) => {
    for (const k of Object.keys(bag)) delete bag[k];
    snap.docs.forEach(d => { bag[d.id] = { id: d.id, ...d.data() }; });
  };

  const unsubRecent = onSnapshot(
    query(colRef(shopId, 'orders'), orderBy('createdAt', 'desc'), limit(max)),
    (snap) => { collect(recent)(snap); gotRecent = true; emit(); },
    fail('recent')
  );

  const unsubUpcoming = onSnapshot(
    query(
      colRef(shopId, 'orders'),
      where('fulfillment.neededDate', '>=', todayStr),
      orderBy('fulfillment.neededDate', 'asc')
    ),
    (snap) => { collect(upcoming)(snap); emit(); },
    // Non-fatal: shops with no fulfillment data yet still get window (1).
    (err) => console.warn('[subscribeRecentOrders:upcoming]', err.message)
  );

  return () => { unsubRecent(); unsubUpcoming(); };
}

/** Mark how far along the arrangement is. This is what empties the board. */
export async function setDesignStatus(shopId, orderId, designStatus, who) {
  const patch = {
    'fulfillment.designStatus': designStatus,
    updatedAt: serverTimestamp(),
  };
  if (designStatus === 'done') {
    patch['fulfillment.designedAt'] = serverTimestamp();
    if (who) patch['fulfillment.designedBy'] = who;
  }
  await updateDoc(docRef(shopId, 'orders', orderId), patch);
}

/** Delivered / picked up / dropped at the airport — it's out of our hands. */
export async function setHandedOff(shopId, orderId, handedOff, who) {
  await updateDoc(docRef(shopId, 'orders', orderId), {
    'fulfillment.handedOff': handedOff,
    ...(handedOff && {
      'fulfillment.handedOffAt': serverTimestamp(),
      ...(who && { 'fulfillment.handedOffBy': who }),
    }),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Cancel an order, recording why, by whom, and whether money has to go back.
 *
 * Status stays 'void' for compatibility with orders cancelled before this
 * existed. `refundDue` is what makes a cancelled paid order visible as money
 * still owed to the customer rather than a silent status flip.
 */
export async function cancelOrder(shopId, orderId, { reason, refundDue, wasPaid, by } = {}) {
  await updateDoc(docRef(shopId, 'orders', orderId), {
    status: 'void',
    cancellation: {
      reason:    reason || '',
      refundDue: !!refundDue,
      // Snapshotted because the status is about to become 'void', which erases
      // the only other evidence that money had been taken.
      wasPaid:   !!wasPaid,
      by:        by || null,
      at:        new Date().toISOString(),
      refundedAt: null,
    },
    updatedAt: serverTimestamp(),
  });
}

/** Money handed back to the customer on a cancelled order. */
export async function markRefunded(shopId, orderId, by) {
  await updateDoc(docRef(shopId, 'orders', orderId), {
    'cancellation.refundedAt': new Date().toISOString(),
    'cancellation.refundedBy': by || null,
    updatedAt: serverTimestamp(),
  });
}

/** Backfill a needed-date onto a legacy order that never had one. */
export async function setNeededDate(shopId, orderId, neededDate, neededTime) {
  await updateDoc(docRef(shopId, 'orders', orderId), {
    'fulfillment.neededDate': neededDate,
    ...(neededTime !== undefined && { 'fulfillment.neededTime': neededTime }),
    updatedAt: serverTimestamp(),
  });
}

// ─── Customers ────────────────────────────────────────────────────────────────

export async function createCustomer(shopId, data) {
  const ref = await addDoc(colRef(shopId, 'customers'), {
    ...data,
    createdAt: serverTimestamp()
  });
  return ref.id;
}

export async function updateCustomer(shopId, customerId, data) {
  await updateDoc(docRef(shopId, 'customers', customerId), {
    ...data,
    updatedAt: serverTimestamp()
  });
}

export async function getCustomers(shopId, searchTerm = '') {
  const snap = await getDocs(query(colRef(shopId, 'customers'), orderBy('lastName')));
  const all  = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  if (!searchTerm) return all;
  const t = searchTerm.toLowerCase();
  return all.filter(c =>
    c.firstName?.toLowerCase().includes(t) ||
    c.lastName?.toLowerCase().includes(t)  ||
    c.phone?.includes(t) ||
    c.email?.toLowerCase().includes(t)
  );
}

export async function getCustomer(shopId, customerId) {
  const snap = await getDoc(docRef(shopId, 'customers', customerId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function deleteCustomer(shopId, customerId) {
  await deleteDoc(docRef(shopId, 'customers', customerId));
}

/** Look up a customer by exact phone match — how a repeat caller gets found. */
export async function getCustomerByPhone(shopId, phone) {
  if (!phone) return null;
  const snap = await getDocs(
    query(colRef(shopId, 'customers'), where('phone', '==', phone), limit(1))
  );
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

const MAX_RECENT_RECIPIENTS = 5;

/** Same person, same address — used to dedupe the recent-recipients list. */
function recipientKey(r) {
  return [r.firstName, r.lastName, r.address]
    .map(s => (s || '').trim().toLowerCase())
    .join('|');
}

/**
 * Create or update the customer record behind an order, and remember who the
 * flowers went to. This is what lets the next order for the same person
 * autofill from just a name or phone number, and offer their past recipients
 * back as a pick-list instead of retyping an address every time.
 *
 * Matching order: an explicit customerId wins; otherwise an exact phone match
 * reuses that record (the common case — a repeat caller); otherwise a new
 * customer is created. Returns the customer id, or null if there was nothing
 * in `billing` to identify anyone by.
 */
export async function upsertCustomerFromOrder(shopId, { customerId, billing, recipient } = {}) {
  const b = billing || {};
  if (!b.firstName && !b.lastName && !b.phone) return null;

  let id = customerId || null;
  let existing = id ? await getCustomer(shopId, id) : null;
  if (!existing && b.phone) {
    existing = await getCustomerByPhone(shopId, b.phone);
    id = existing?.id || null;
  }

  const patch = {
    firstName: b.firstName || existing?.firstName || '',
    lastName:  b.lastName  || existing?.lastName  || '',
    phone:     b.phone     || existing?.phone     || '',
    email:     b.email     || existing?.email     || '',
    address:   b.address   || existing?.address   || '',
    city:      b.city      || existing?.city      || '',
    stPr:      b.stPr      || existing?.stPr      || '',
    zipPostal: b.zipPostal || existing?.zipPostal || '',
    country:   b.country   || existing?.country   || '',
  };

  if (recipient?.firstName || recipient?.lastName) {
    const entry = {
      firstName: recipient.firstName || '', lastName: recipient.lastName || '',
      address:   recipient.address   || '', city:     recipient.city     || '',
      stPr:      recipient.stPr      || '', zipPostal:recipient.zipPostal|| '',
      country:   recipient.country   || '', phone:    recipient.phone    || '',
      occasion:  recipient.occasion  || '',
    };
    const key = recipientKey(entry);
    const rest = (existing?.recentRecipients || []).filter(r => recipientKey(r) !== key);
    patch.recentRecipients = [entry, ...rest].slice(0, MAX_RECENT_RECIPIENTS);
  }

  if (id) { await updateCustomer(shopId, id, patch); return id; }
  return createCustomer(shopId, patch);
}

// ─── Products ─────────────────────────────────────────────────────────────────

export async function createProduct(shopId, data) {
  const ref = await addDoc(colRef(shopId, 'products'), {
    ...data,
    active: true,
    createdAt: serverTimestamp()
  });
  return ref.id;
}

export async function updateProduct(shopId, productId, data) {
  await updateDoc(docRef(shopId, 'products', productId), {
    ...data,
    updatedAt: serverTimestamp()
  });
}

export async function getProducts(shopId) {
  const snap = await getDocs(query(colRef(shopId, 'products'), orderBy('name')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function deleteProduct(shopId, productId) {
  await deleteDoc(docRef(shopId, 'products', productId));
}

// ─── Employees ────────────────────────────────────────────────────────────────

export async function createEmployee(shopId, data) {
  const ref = await addDoc(colRef(shopId, 'employees'), {
    ...data,
    role: data.role || 'staff',
    status: 'invited',
    createdAt: serverTimestamp()
  });
  return ref.id;
}

export async function getEmployees(shopId) {
  const snap = await getDocs(colRef(shopId, 'employees'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function updateEmployee(shopId, empId, data) {
  await updateDoc(docRef(shopId, 'employees', empId), data);
}

export async function deleteEmployee(shopId, empId) {
  await deleteDoc(docRef(shopId, 'employees', empId));
}

// ─── Reports / Sales Summary ──────────────────────────────────────────────────

export async function getSalesSummary(shopId) {
  const now       = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const yearStart  = new Date(now.getFullYear(), 0, 1);

  const snap = await getDocs(query(
    colRef(shopId, 'orders'),
    where('status', 'in', ['completed', 'paid']),
    orderBy('createdAt', 'desc')
  ));
  const orders = snap.docs.map(d => ({ id: d.id, ...d.data() }));

  const types = ['local_delivery', 'wire_out', 'wire_in', 'carry_out', 'pick_up'];
  const labels = {
    local_delivery: 'Local Delivery',
    wire_out:       'Wire Out',
    wire_in:        'Wire In',
    carry_out:      'Carry Out',
    pick_up:        'Pick Up',
  };

  const toDate = (ts) => ts?.toDate ? ts.toDate() : new Date(ts);

  const summarize = (orders, since) => {
    const filtered = since ? orders.filter(o => toDate(o.createdAt) >= since) : orders;
    return {
      count: filtered.length,
      total: filtered.reduce((sum, o) => sum + (o.total || 0), 0)
    };
  };

  return types.map(type => {
    const filtered = orders.filter(o => o.type === type);
    return {
      type,
      label: labels[type],
      today:   summarize(filtered, todayStart),
      month:   summarize(filtered, monthStart),
      year:    summarize(filtered, yearStart),
    };
  });
}
